// M6.6 — FlashcardBrowsePage: tra cứu tự do theo bộ + chủ đề (R-P0-D7 / AC-FC-7).
// NV chọn bộ (1000k / 1250k / nâng cao / nội quy), tuỳ chọn topic để lọc,
// xem danh sách card kèm state hiện tại (new / learning / review) + ngày ôn kế.
// Dùng lại api.topicsList(bo) để lấy topic options.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { boLabel, boShort } from '../lib/bo.js';
import { showConfirm } from '../lib/dialogs.js';
import { renderRichText } from '../lib/renderRichText.js';

const ALLOWED_SETS = ['bo_1000k', 'bo_1250k', 'bo_nang_cao', 'bo_noi_quy'];

const STATE_BADGE = {
  new:      { label: 'Chưa học',  cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  learning: { label: 'Đang học',  cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  review:   { label: 'Đã thuộc',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

export default function FlashcardBrowsePage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSet = ALLOWED_SETS.includes(searchParams.get('set') || '')
    ? searchParams.get('set')
    : 'bo_1000k';
  const initialTopic = searchParams.get('topic') || '';

  const [setName, setSetName] = useState(initialSet);
  const [topic, setTopic] = useState(initialTopic);
  const [topics, setTopics] = useState([]);        // [{ topic, sort_order }]
  const [items, setItems] = useState([]);          // card list
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');                  // free-text filter local
  // M321 (2026-05-29): bị khoá tra cứu vì cửa hàng đang có người làm bài thi
  const [lockedMsg, setLockedMsg] = useState(null);

  // Load topics khi đổi bộ
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.topicsList(setName);
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : data.topics || data.items || [];
        setTopics(rows);
      } catch {
        setTopics([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setName]);

  // Load cards khi đổi bộ / topic
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await api.flashcardBrowse(setName, topic || null);
        if (cancelled) return;
        setItems(data.items || []);
        setLockedMsg(null);
      } catch (err) {
        if (cancelled) return;
        // M321: cửa hàng đang có người thi → hiển thị panel khoá thay vì toast lỗi
        if (err.data?.error === 'BROWSE_LOCKED_EXAM_ACTIVE') {
          setLockedMsg(err.data.message || 'Tính năng tra cứu tạm khoá vì cửa hàng đang có người làm bài kiểm tra.');
          setItems([]);
        } else {
          toast.error(err.data?.error || 'Không tải được danh sách');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setName, topic]);

  // Sync URL
  useEffect(() => {
    const qs = {};
    if (setName && setName !== 'bo_1000k') qs.set = setName;
    else qs.set = setName;
    if (topic) qs.topic = topic;
    setSearchParams(qs, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setName, topic]);

  // Local free-text filter (không gọi API)
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) =>
        (it.question || '').toLowerCase().includes(needle) ||
        (it.answer || '').toLowerCase().includes(needle)
    );
  }, [items, q]);

  const stats = useMemo(() => {
    const s = { new: 0, learning: 0, review: 0 };
    for (const it of items) {
      const st = it.state || 'new';
      if (st in s) s[st]++;
    }
    return s;
  }, [items]);

  async function doReset(cardId) {
    const ok = await showConfirm({
      title: 'Reset thẻ',
      message: 'Reset thẻ này về trạng thái "Chưa học"?',
      confirmText: 'Reset',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.flashcardReset(cardId);
      toast.success('Đã reset');
      // refresh danh sách
      const data = await api.flashcardBrowse(setName, topic || null);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.data?.error || 'Reset thất bại');
    }
  }

  return (
    <div className="min-h-full p-6">
      <div className="max-w-5xl mx-auto">
        <header className="mb-4">
          <button
            onClick={() => nav('/')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            ← Trang chủ
          </button>
          <div className="flex items-baseline justify-between mt-1">
            <h1 className="text-xl font-semibold text-brand-700">Tra cứu flashcard</h1>
            <Link to="/flashcards" className="text-sm text-brand-600 hover:underline">
              ↩ Quay lại học
            </Link>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Duyệt toàn bộ card theo bộ, kèm trạng thái ôn hiện tại của bạn.
          </p>
        </header>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Bộ</label>
              <select
                value={setName}
                onChange={(e) => {
                  setSetName(e.target.value);
                  setTopic(''); // reset topic khi đổi bộ
                }}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              >
                {ALLOWED_SETS.map((s) => (
                  <option key={s} value={s}>
                    {boLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Chủ đề</label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              >
                <option value="">(Tất cả)</option>
                {topics.map((t) => (
                  <option key={t.topic} value={t.topic}>
                    {t.topic}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-500">Tìm từ khoá</label>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Gõ để lọc theo câu hỏi hoặc đáp án…"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              />
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
            <span>Tổng: {items.length}</span>
            <span>·</span>
            <span className="text-slate-600">Chưa học: {stats.new}</span>
            <span>·</span>
            <span className="text-amber-700">Đang học: {stats.learning}</span>
            <span>·</span>
            <span className="text-emerald-700">Đã thuộc: {stats.review}</span>
          </div>
        </div>

        {/* M321: Panel khoá khi cửa hàng đang có người làm bài thi */}
        {lockedMsg ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="browse-locked-panel">
            <div className="text-4xl mb-2">🔒</div>
            <h2 className="text-base font-semibold text-amber-800 mb-1">Tạm khoá tra cứu</h2>
            <p className="text-sm text-amber-700 max-w-md mx-auto">{lockedMsg}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700"
            >
              🔄 Thử lại
            </button>
          </div>
        ) : loading ? (
          <div className="text-center text-slate-400 py-10">Đang tải…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-slate-400 py-10">
            {q ? 'Không có card nào khớp từ khoá.' : 'Bộ này chưa có card.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((it) => (
              <CardRow key={it.id} item={it} onReset={() => doReset(it.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CardRow({ item, onReset }) {
  const [open, setOpen] = useState(false);
  const state = item.state || 'new';
  const badge = STATE_BADGE[state] || STATE_BADGE.new;
  const next = item.next_review_at ? formatDateShort(item.next_review_at) : '—';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs mb-1">
            <span className={`px-2 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
            {item.topic && (
              <span className="text-slate-500 font-mono text-[11px]">{item.topic}</span>
            )}
            <span className="text-slate-400 text-[11px]">#{item.id}</span>
          </div>
          <div className="text-sm text-slate-800 line-clamp-2 rich-content"
               dangerouslySetInnerHTML={{ __html: renderRichText(item.question) }} />
        </div>
        <div className="text-xs text-slate-400 shrink-0 text-right">
          <div>Ôn kế: {next}</div>
          <div className="mt-0.5">{open ? '▲ đóng' : '▼ xem đáp án'}</div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0">
          {item.answer ? (
            <div className="rich-content p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700"
                 dangerouslySetInnerHTML={{ __html: renderRichText(item.answer) }} />
          ) : (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700">
              <span className="italic text-slate-400">(Không có đáp án)</span>
            </div>
          )}
          <div className="mt-2 flex items-center justify-between text-xs">
            <div className="text-slate-500">
              Lần ôn cuối: {item.last_review_at ? formatDateShort(item.last_review_at) : '—'}
              {typeof item.ease_factor === 'number' && (
                <span className="ml-2 text-slate-400">
                  · EF {item.ease_factor.toFixed(2)} · lặp {item.repetitions ?? 0}
                </span>
              )}
            </div>
            {state !== 'new' && (
              <button
                onClick={onReset}
                className="text-rose-600 hover:text-rose-800 hover:underline"
              >
                Reset về "Chưa học"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDateShort(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
